/**
 * @file ElementFontNote.tsx
 * @description Подсказка «какой шрифт у этого элемента» (phase19.16, приёмка
 * Р2а, §7.17 п. 3; SB10, дополнение 2026-10-11; uiux-fonts-usage §4, §6):
 * «Аа Шрифт заголовков · Montserrat · общий для всего сайта» и «Сменить
 * шрифт →» во «Оформление → Шрифты» с раскрытой ролью и возвратом.
 *
 * SB122 (uiux-settings-groups §3): переход в другой раздел — знак «→»
 * («›» — только внутри панели); подпись «Шрифты элемента» — строка справки,
 * не заголовок группы (`SectionLabel` в панелях конструктора не используется).
 * В панели элемента справка стоит в группе «Цвет и шрифт» / «Шрифт».
 *
 * Одно место во всех панелях: в группе «Цвет и шрифт» / «Шрифт» перед
 * «Расположением» (в стиле `ProductNote`). Части и роли — `elementFontParts` из
 * shared по текущей форме: у «Надписи» строка следует за стилем. Те же
 * строки — в «Каталог → Карточка товара», шапке категории, шапке раздела
 * и подписи подвала. Текста у элемента не бывает — подсказки нет.
 */

import type { FontPart } from "@chaiberi/shared";
import { Link } from "react-router";

import { cn } from "@/lib/utils";
import { JUMP_LINK_CLASS } from "../../../ui/jump-link";
import { FontPartLine } from "./FontPartLine";
import { useElementFonts, type ElementFontsModel } from "../../../../hooks/site-builder/useElementFonts";
import { useFontsLink } from "../../../../hooks/site-builder/useFontsLink";
import type { BuilderSelection } from "../../../../lib/site-builder/selection";

export const NO_THEME_RIGHT_TEXT = "Шрифты сайта меняет сотрудник с правом на оформление";

interface Props {
  parts: readonly FontPart[];
  /** Подпись возврата во «Шрифтах»: «Заголовок (секция «Новинки»)». */
  backLabel: string;
  /** Куда вернуться, если не на текущий адрес (вкладка каталога). */
  back?: Partial<BuilderSelection>;
}

/** Текст кнопки по состоянию: акцент не выбран, список не получен, одна или несколько ролей. */
function actionLabel(model: ElementFontsModel): string {
  if (model.listFailed) return "Открыть шрифты →";
  if (model.lines.length === 1) return model.lines[0]?.fallback ? "Выбрать →" : "Сменить шрифт →";
  return "Сменить шрифты →";
}

export function ElementFontNote({ parts, backLabel, back }: Props) {
  const model = useElementFonts(parts);
  const link = useFontsLink(model?.firstRole ?? "heading", backLabel, back);
  if (!model) return null;
  const single = model.lines.length === 1;

  return (
    <section aria-label="Шрифты элемента" className="type-caption flex flex-col gap-2 rounded-lg bg-surface-low px-3 py-2 text-on-surface-variant">
      {single ? null : <p className="font-medium text-on-surface">Шрифты элемента</p>}
      <ul className="flex flex-col gap-1.5">
        {model.lines.map((line) => (
          <FontPartLine key={`${line.part}:${line.role}`} line={line} theme={model.theme} registry={model.registry} withStyle={single} />
        ))}
      </ul>
      <p>{single ? "Общий для всего сайта" : "Общие для всего сайта"}</p>
      {model.canEdit ? (
        <Link to={link.to} onClick={link.onClick} className={cn(JUMP_LINK_CLASS, "self-end")}>
          {actionLabel(model)}
        </Link>
      ) : (
        <p>{NO_THEME_RIGHT_TEXT}</p>
      )}
    </section>
  );
}
